import { useVuexGetters } from '@scfe-common/micro-frontend-utils';
import { ColumnChart } from '@ssc-ui/charts-react';
import * as React from 'react';
import { ProFilter } from 'react-pro-components';
import { Form, message, Skeleton } from 'ssc-ui-react';
import { IconHelpOutline } from 'ssc-ui-react/es/components/icon';

import { $gt } from '@/common/appCtx';
import { getTimeInterval, transformNum } from '@/pages/workstation-dashboard/utils/common';

import { CacheType, LastTenHoursGroupThroughputTooltip, MAX_SELECTED_WORKSTATION } from '../../const';
import useDropdownListStore from '../../store/use-dropdown-list';
import { useLastTenHoursGroupThroughputStore } from '../../store/use-last-ten-hours-group-throughput';
import useWorkstationOverviewStore from '../../store/use-workstation-overview';
import { dashboardDBManager } from '../../utils/storage';
import styles from './index.module.less';

const DEFAULT_INTERVAL = '3 p.m. - 4 p.m.';
const DEFAULT_INTERVAL_THROUGH = NaN;

const LastTenHoursGroupThroughput: React.FC = () => {
  const [form1] = Form.useForm();
  const [form2] = Form.useForm();
  const [selectedWorkstation, setSelectedWorkstation] = React.useState<string[]>([]);
  const [showSelectedWorkstation, setShowSelectedWorkstation] = React.useState<string[]>([]);
  const workstation_group_id_list = Form.useWatch('workstation_group_id_list', form2);
  const targetGroup = useWorkstationOverviewStore.use.targetGroup();
  const loading = useLastTenHoursGroupThroughputStore.use.loading();
  const list = useLastTenHoursGroupThroughputStore.use.throughputList();
  const setThroughputForm = useLastTenHoursGroupThroughputStore.use.setThroughputForm();
  const getGroupLastTenHoursThroughputAsync = useLastTenHoursGroupThroughputStore.use.getGroupLastTenHoursThroughputAsync();
  const groupDropdownList = useDropdownListStore.use.lastTenHoursGroupDropdownList();
  const setGroupDropdownList = useDropdownListStore.use.setLastTenHoursGroupDropdownList();
  const groupDropdownListLoading = useDropdownListStore.use.lastTenHoursGroupDropdownListLoading();
  const groupIdMap = useDropdownListStore.use.lastTenHoursGroupIdMap();
  const timeIntervals = useWorkstationOverviewStore.use.timeIntervals();
  const workstationDashboardMockBeginTime = useVuexGetters('workforceGlobalDataOps/workstationDashboardMockBeginTime');

  const showGroupDropdownList = React.useMemo(() => {
    return groupDropdownList.filter(item => item.label !== form1.getFieldValue('selected_group'));
  }, [form1, groupDropdownList]);

  const lastTenHoursThroughputDisplayData = React.useMemo(() => {
    // should return items for xAxis display
    const datasource = list?.map((item,) => {
      return item?.item_list?.map(subItem => {
        const timeBegin = (subItem.time_begin ?? 0);
        const type = getTimeInterval(timeBegin, timeIntervals, workstationDashboardMockBeginTime) ?? '';
        if (!type) return undefined;
        return {
          type,
          throughput: subItem.throughput ?? 0,
          workstation_group: item.workstation_group,
        };
      }) ?? [];
    }).flat();
    timeIntervals.forEach(interval => {
      const exists = datasource.some(data => data?.type === interval);
      if (!exists) {
        datasource.push({
          type: interval,
          throughput: 0,
          workstation_group: targetGroup?.workstation_group || '',
        });
      }
    });

    // 勾选了但未在展示范围内的group;
    selectedWorkstation.forEach(selected => {
      const exists = datasource.some(data => data?.workstation_group === selected);
      if (!exists) {
        datasource.push({
          type: DEFAULT_INTERVAL,
          throughput: DEFAULT_INTERVAL_THROUGH,
          workstation_group: selected || ''
        });
      }
    });
    return datasource.filter(Boolean);
  }, [list, targetGroup?.workstation_group, timeIntervals, workstationDashboardMockBeginTime, selectedWorkstation]);

  const setCache = React.useCallback((model: {workstation_group_id_list: number[]}) => {
    const data = {
      workstation_group_list: model.workstation_group_id_list.map(groupId => ({
        value: groupId,
        label: groupIdMap?.[groupId],
      })),
    };
    dashboardDBManager.put(CacheType.Last10HoursGroupThroughput, data);
  }, [groupIdMap]);

  const getCache = React.useCallback(async () => {
    try {
      const cache = await dashboardDBManager.get(CacheType.Last10HoursGroupThroughput) as {workstation_group_list?: {value: number; label: string}[]} | undefined;
      const groupList = cache?.workstation_group_list;
      if (groupList?.length) {
        const disabledCaches: {value: number; label: string; disabled?: boolean}[] = [];
        const workstationGroupIdList: number[] = [];
        groupList.forEach(item => {
          if (!groupIdMap[item.value]) {
            disabledCaches.push({
              ...item,
              disabled: true,
            });
          }
          workstationGroupIdList.push(item.value);
        });
        setGroupDropdownList(disabledCaches, true);
        form2.setFieldValue('workstation_group_id_list', workstationGroupIdList);
      } else {
        form2.setFieldValue('workstation_group_id_list', []);
      }
    } catch (e) {
      console.error('[LastTenHoursGroupThroughput] getCache error: ', e);
    } finally {
      setThroughputForm(form2);
    }
  }, [form2, groupIdMap, setGroupDropdownList, setThroughputForm]);

  React.useEffect(() => {
    if (!targetGroup || !targetGroup.groupId) {
      form1.setFieldValue('selected_group', '');
      setSelectedWorkstation(prev => prev.filter(item => item !== targetGroup.workstation_group));
    } else {
      if (!targetGroup.workstation_group) return;
      form1.setFieldValue('selected_group', targetGroup.workstation_group);
      setSelectedWorkstation(prev => {
        if (!targetGroup.workstation_group || prev.includes(targetGroup.workstation_group)) return prev;
        return [targetGroup.workstation_group, ...prev];
      });
    }
  }, [form1, targetGroup]);

  React.useEffect(() => {
    if (!selectedWorkstation?.length) {
      return setShowSelectedWorkstation([]);
    }

    setShowSelectedWorkstation(prev => {
      if (prev.length === MAX_SELECTED_WORKSTATION) return prev;
      return [...prev, ...selectedWorkstation.filter(st => !prev.includes(st))].filter(Boolean).slice(0, MAX_SELECTED_WORKSTATION);
    });
  }, [selectedWorkstation]);

  React.useLayoutEffect(() => {
    getCache();
  }, [getCache]);

  React.useEffect(() => {
    const workstationList = [
      ...(targetGroup ? [targetGroup.workstation_group] : []),
      ...(workstation_group_id_list?.map((item: number) => groupIdMap?.[item]) || []),
    ];
    setSelectedWorkstation(workstationList);
  }, [workstation_group_id_list, groupIdMap, targetGroup]);

  return (
    <div className={styles.tenHoursGroupThroughput}>
      <Form.Legend
        level={2}
        tooltip={{
          title: LastTenHoursGroupThroughputTooltip,
          icon: <IconHelpOutline />,
        }}
      >
        {$gt('Last 10 Hours Group Throughput')}
      </Form.Legend>
      <div className={styles.selectedGroups}>
        <ProFilter
          formProps={{ form: form1 }}
          columns={3}
          fields={[
            {
              type: 'input',
              name: 'selected_group',
              label: $gt('Selected Group'),
              style: { width: '100%' },
              ctrlProps: {
                disabled: true,
              },
            },
          ]}
        />
        <div className={styles.vs}>{$gt('V.S.')}</div>
        <ProFilter
          formProps={{ form: form2 }}
          columns={3}
          fields={[
            {
              type: 'select',
              name: 'workstation_group_id_list',
              label: $gt('Comparison Group'),
              style: { width: '100%' },
              ctrlProps: {
                mode: 'multiple',
                maxTagCount: 1,
                maxTagTextWidth: 80,
                allowClear: true,
                filterOption: true,
                loading: groupDropdownListLoading,
                options: showGroupDropdownList,
              }
            }
          ]}
          onChange={(model: {workstation_group_id_list: number[]}) => {
            if (!targetGroup) return;
            setCache(model);
            getGroupLastTenHoursThroughputAsync({
              workstation_group_id_list: [targetGroup?.groupId, ...model.workstation_group_id_list],
            });
          }}
        />
      </div>
      <div className={styles.chartWrapper}>
        {loading ? (
          <Skeleton.Chart />
        ) : (
          <ColumnChart
            dataSource={lastTenHoursThroughputDisplayData}
            echartsOption={{
              tooltip: {
                show: !!list?.length,
                formatter: (params) => {
                  if (Array.isArray(params) && params.length) {
                    const map = lastTenHoursThroughputDisplayData.reduce((acc, item) => {
                      if (!item) return acc;
                      acc[`${item.type}-${item.workstation_group}`] = item;
                      return acc;
                    }, {} as Record<string, { type: string; throughput?: number; workstation_group?: string }>);
                    if (params.every(item => map[item.seriesName || '']?.throughput === 0)) {
                      return '';
                    }
                    const title = params[0].name;
                    const head = `
                      <div style="font-weight: bold; font-size: 16px;">
                        ${title}
                      </div>
                    `;
                    const content = params.map(item => {
                      const target = map[`${item.name}-${item.seriesName}`];
                      const { throughput } = target || {};
                      if (throughput === DEFAULT_INTERVAL_THROUGH) return '';
                      return `
                        <div style="display: flex; align-items: baseline; justify-content: space-between; margin-top: 12px; font-size: 14px;">
                          ${item.marker}
                          <div style="width: 180px; margin-right: 10px; color: #666; white-space: normal; word-break: break-all;">
                            ${item.seriesName}
                          </div>
                          <div style="width: 100px; color: #333; white-space: normal; word-break: break-all;">
                            ${transformNum(throughput)}
                          </div>
                        </div>
                      `;
                    }).join('');
                    return `
                      ${head}
                      <div style="width: 280px;">
                        ${content}
                      </div>
                    `;
                  }
                  return '';
                }
              },
              yAxis: [
                {
                  splitLine: {
                    show: true,
                    lineStyle: { type: 'dashed' },
                  },
                  type: 'value',
                  min: 0,
                  max: list?.length ? undefined : 5000,
                  axisLabel: {
                    formatter: (value: number) => transformNum(value)
                  }
                }
              ],
              xAxis: [
                {
                  type: 'category',
                  axisTick: {
                    show: false,
                  },
                }
              ],
            }}
            config={{
              xField: 'type',
              yField: 'throughput',
              seriesField: 'workstation_group',
              legend: {
                type: 'checkbox',
                show: true,
                selected: showSelectedWorkstation,
                filter: (item) => {
                  return item.name !== 'default';
                },
                onChange: (selectedKeys: string[]) => {
                  if (selectedKeys.length > MAX_SELECTED_WORKSTATION) {
                    message.error($gt('Display limit reached (max 9 bars). Please deselect some items and try again.'));
                    return;
                  }
                  // 若取消勾选的是selected group，不允许取消勾选
                  const selectedGroup = form1.getFieldValue('selected_group');
                  if (!selectedKeys.includes(selectedGroup)) {
                    setShowSelectedWorkstation([selectedGroup, ...selectedKeys]);
                    return;
                  }
                  setShowSelectedWorkstation(selectedKeys);
                },
              },
            }}
          />
        )}
      </div>
    </div>
  );
};

export default LastTenHoursGroupThroughput;
